import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Flex,
  Text,
  Button,
  Select,
  Separator,
  IconButton,
} from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveBrevoMappings } from "hooks";

export function BrevoConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();

  // Brevo-provided lists (from location.state)
  const [brevoLists, setBrevoLists] = useState([]);

  // bconnect data
  const { data: brandsData, isLoading: isLoadingBrands } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();

  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // single mapping state keyed by bconnect_location_id -> { brevo_list_id, isMapped }
  const [locationMappings, setLocationMappings] = useState({});
  // Per-brand temporary selection for "apply to brand"
  const [brandListSelections, setBrandListSelections] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);

  const saveBrevoMappings = useSaveBrevoMappings();

  // Normalize Brevo lists from location.state
  useEffect(() => {
    const raw = location.state?.lists;
    const listsFromState = Array.isArray(raw) ? raw : [];
    setBrevoLists(
      (listsFromState || []).map((l) => ({ id: String(l.id ?? ""), name: l.name }))
    );
  }, [location.state]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = locationsData.filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Handlers for per-location mapping (centralized)
  const handleLocationListChange = (bconnectLocationId, brevoListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { brevo_list_id: "", isMapped: false };
      next[key] = { ...existing, brevo_list_id: brevoListId, isMapped: Boolean(brevoListId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => {
      const next = { ...(prev || {}) };
      next[String(brandId)] = listId;
      return next;
    });
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { brevo_list_id: "", isMapped: false };
        next[key] = { ...existing, brevo_list_id: selectedList, isMapped: Boolean(selectedList) };
      });
      return next;
    });
  };

  // Save handler: call backend API to save mappings
  const handleSaveLocationMappings = async (section) => {
    try {
      setIsLoading(true);
      setProgressValue(10);

      // Build payload that would be sent to backend
      const allLocations = [
        ...(independentLocations || []),
        ...(Object.values(brandLocationsMap || {}).flat() || []),
      ];

      const locationPayload = (allLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.brevo_list_id || null;

          if (!currList) return null;

          const brevoListObj = brevoLists.find((bl) => String(bl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            brevo_list_id: currList,
            brevo_list_name: brevoListObj?.name || "",
          };
        })
        .filter(Boolean);

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
      };

      setProgressValue(50);

      const result = await saveBrevoMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      setProgressValue(80);
      queryClient.invalidateQueries(["brevo-status"]);
      queryClient.invalidateQueries(["brevo-mappings", userProfile.organisation_id]);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected Brevo lists.",
          integrationType: "brevo",
        },
      });
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving Brevo location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  if (!location.state) {
    return <div>No authentication data found. Please go back and log your credentials again.</div>;
  }

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "1000px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Header */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
            <Flex display="flex" align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img src={require("../../../assets/images/klaviyo.jpg")} alt="brevo" style={{ width: "24px" }} />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">Brevo</Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Map b connect locations to Brevo lists (map each location independently).
                </Text>
              </Flex>
            </Flex>

            <Flex display="flex" align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate(-1)} color="gray">Cancel</Button>
              <Button
                size="2"
                variant="soft"
                onClick={() => handleSaveLocationMappings("all")}
                loading={isLoading}
              >
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          {/* BRANDS section */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5" mb="4">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Brands</Text>
              <Text size="1" weight="regular">Group: b connect brands with each of their locations (map each location independently).</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "48%" }}>Brevo list</Text>
              </Flex>

              {(bconnectBrands || []).length > 0 ? (
                (bconnectBrands || []).map((b) => {
                  const brandLocations = brandLocationsMap[b.id] || [];
                  return (
                    <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                      <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                        <Text
                          size="2"
                          weight="medium"
                          style={{
                            flex: "1 1 0%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          [{b.name}]
                        </Text>

                        {/* Brand-level select: apply a Brevo list to all locations under this brand */}
                        {(brandLocations || []).length > 0 && (
                          <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                            <Select.Root
                              value={brandListSelections[String(b.id)] || ""}
                              onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                              style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                            >
                              <Select.Trigger placeholder="Apply list to brand..." style={{ width: "220px", boxSizing: "border-box" }}>
                                <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                  title={brandListSelections[String(b.id)] ? (brevoLists.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply list to brand..."}
                                >
                                  {brandListSelections[String(b.id)] ? (brevoLists.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply list to brand..."}
                                </div>
                              </Select.Trigger>
                              <Select.Content side="bottom" align="start" sideOffset={4}>
                                {brevoLists.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                              </Select.Content>
                            </Select.Root>

                            <Button size="1" variant="soft" onClick={() => handleApplyBrandList(b.id)} style={{ flexShrink: 0 }}>
                              Apply
                            </Button>
                          </Flex>
                        )}
                      </Flex>

                      {(brandLocations || []).length > 0 ? (
                        brandLocations.map((loc) => {
                          const locId = loc.id ?? loc.value ?? loc.location_id;
                          const key = String(locId);
                          const mapping = locationMappings[key] || {};

                          return (
                            <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                              <Flex style={{ width: "48%", minWidth: 0 }}>
                                <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                  <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                    {loc.name ?? loc.label ?? ""}
                                  </Text>
                                </Flex>
                              </Flex>

                              <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                                <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                  <ArrowRightIcon width="16" height="16" />
                                </IconButton>
                              </Flex>

                              <Flex style={{ width: "48%", minWidth: 0 }}>
                                <Select.Root value={mapping.brevo_list_id || ""} onValueChange={(v) => handleLocationListChange(key, v)} style={{ width: "100%" }}>
                                  <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                    <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.brevo_list_id ? (brevoLists.find((bl) => String(bl.id) === String(mapping.brevo_list_id))?.name || "") : "Select a Brevo list"}>
                                      {mapping.brevo_list_id ? (brevoLists.find((bl) => String(bl.id) === String(mapping.brevo_list_id))?.name || "") : "Select a Brevo list"}
                                    </div>
                                  </Select.Trigger>
                                  <Select.Content side="bottom" align="start" sideOffset={4}>
                                    {brevoLists.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                                  </Select.Content>
                                </Select.Root>
                              </Flex>
                            </Flex>
                          );
                        })
                      ) : (
                        <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                        </Flex>
                      )}
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No brands found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="2" />

          {/* LOCATIONS (independent / default-brand locations) */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
            <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
              <Text size="2" weight="medium">Locations</Text>
              <Text size="1" weight="regular">Map independent b connect locations to a Brevo list.</Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              <Flex direction="row" justify="between" style={{ width: "100%" }}>
                <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
                <Text size="2" weight="medium" style={{ width: "48%" }}>Brevo list</Text>
              </Flex>

              {(independentLocations || []).length > 0 ? (
                (independentLocations || []).map((loc) => {
                  const locId = loc.id ?? loc.value ?? loc.location_id;
                  const key = String(locId);
                  const mapping = locationMappings[key] || {};
                  return (
                    <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                      <Flex style={{ width: "48%", minWidth: 0 }}>
                        <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                          <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                            {loc.name ?? loc.label ?? ""}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "48%", minWidth: 0 }}>
                        <Select.Root value={mapping.brevo_list_id || ""} onValueChange={(v) => handleLocationListChange(key, v)} style={{ width: "100%" }}>
                          <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                            <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.brevo_list_id ? (brevoLists.find((bl) => String(bl.id) === String(mapping.brevo_list_id))?.name || "") : "Select a Brevo list"}>
                              {mapping.brevo_list_id ? (brevoLists.find((bl) => String(bl.id) === String(mapping.brevo_list_id))?.name || "") : "Select a Brevo list"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            {brevoLists.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                          </Select.Content>
                        </Select.Root>
                      </Flex>
                    </Flex>
                  );
                })
              ) : (
                <Flex justify="center" style={{ padding: "20px 0" }}>
                  <Text>No locations found.</Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
